import { ref } from 'vue'
import { Api } from "@/service/api"
import { parse } from "date-fns"

const BITRIX_SELF_TYPES = new Set([
    'BitrixDealsSource',
    'BitrixLeadsSource',
    'BitrixContactsSource',
])

export function useConnectionLoader(route) {
    const isLoading = ref(false)
    const uploadOptions = ref({
        offsetOperation: [
            { name: 'Плюс', type: 'PLUS' },
            { name: 'Минус', type: 'MINUS' },
        ],
        offsetNames: [
            { name: 'Дней', type: 'DAY' },
            { name: 'Месяцев', type: 'MONTH' },
            { name: 'Лет', type: 'YEAR' },
        ]
    })

    async function loadConnection(
        connection,
        selectedYandexAccount,
        yandexUpload,
        selectedVKAccount,
        vkAdsUpload,
        sourceType,
        setYandexAccount,
        setYandexUpload,
        setVKAccount,
        setVKUpload,
        setBitrixAccount,
        setBitrixUpload,
    ) {
        isLoading.value = true
        const workflowId = route.params.workflow_id
        const connectionId = route.params.connection_id

        try {
            const connectionResponse = await Api.getConnection(workflowId, connectionId)
            const newYandexAccounts = await Api.getUserYandexAcc()
            const newVKAccounts = await Api.getVkAccounts()
            const criteriaResponse = await Api.getFilterCriteria()

            connection.value.name = connectionResponse.name
            connection.value.files_source = []
            connection.value.filter.conditionType = connectionResponse.filter?.condition_type || "AND"

            connectionResponse.sources.forEach((source) => {
                if (source?.self_type === "YandexDirectCampaignStatistics") {
                    mapYandexSource(
                        source,
                        newYandexAccounts,
                        selectedYandexAccount,
                        yandexUpload,
                        sourceType,
                        setYandexAccount,
                        setYandexUpload
                    )
                }
                if (source?.self_type === "VKontakteCampaignStatistics") {
                    mapVKSource(
                        source,
                        newVKAccounts,
                        selectedVKAccount,
                        vkAdsUpload,
                        sourceType,
                        setVKAccount,
                        setVKUpload
                    )
                }
                if (source?.self_type === "GoogleSheetSource") {
                    mapGoogleSheetSource(source, connection, sourceType)
                }
                if (isBitrixSource(source)) {
                    mapBitrixSource(
                        source,
                        sourceType,
                        setBitrixAccount,
                        setBitrixUpload,
                        criteriaResponse,
                    )
                }
            })

            if (connectionResponse.target?.self_type === "GoogleSheetTarget") {
                mapGoogleSheetTarget(connectionResponse.target, connection)
            }

            if (connectionResponse.filter !== null) {
                mapFilters(connectionResponse.filter, connection, criteriaResponse)
            }

            await syncTabs(connection)

            return connectionResponse
        } catch (error) {
            console.error('Error loading connection:', error)
            throw error
        } finally {
            isLoading.value = false
        }
    }

    function isBitrixSource(source) {
        if (!source) {
            return false
        }

        return source.source_type === 'BITRIX_UPLOAD'
            || source.obj_type === 'BITRIX_UPLOAD'
            || BITRIX_SELF_TYPES.has(source.self_type)
    }

    function mapUploadDate({
        dateValue,
        offsetValue,
        offsetType,
    }) {
        const result = { type: 'exact', date: null }

        if (dateValue !== null && dateValue !== undefined && dateValue !== '') {
            result.date = parse(dateValue, 'yyyy-MM-dd', new Date())
            result.type = 'exact'
            return result
        }

        if (offsetValue !== null && offsetValue !== undefined && offsetValue !== 0) {
            result.type = 'offset'
            result.length = Math.abs(offsetValue)
            const operationType = offsetValue < 0 ? 'MINUS' : 'PLUS'
            result.offsetOperation = uploadOptions.value.offsetOperation.find(
                (item) => item.type === operationType
            )
            if (offsetType !== null && offsetType !== undefined) {
                result.offsetName = uploadOptions.value.offsetNames.find(
                    (item) => item.type === offsetType
                ) || { name: offsetType, type: offsetType }
            }
        }

        return result
    }

    function mapBitrixConditions(conditions = [], criteriaResponse = []) {
        return (conditions || []).map((condition, index) => {
            const criteria = criteriaResponse.find(
                (item) => item.criteria_type === condition.criteria
            ) || {
                criteria_type: condition.criteria,
                value_type: condition.value_type,
            }

            return {
                id: Date.now() + index,
                field: {
                    id: condition.field_id,
                    name: condition.field_id,
                },
                criteria,
                filter_value: {
                    type: condition.value_type || criteria.value_type || '',
                    data: condition.value ?? '',
                },
            }
        })
    }

    function mapIdOptions(ids = []) {
        return (ids || []).map((id) => ({
            id: String(id),
            name: String(id),
        }))
    }

    function mapBitrixSource(
        source,
        sourceType,
        setBitrixAccount,
        setBitrixUpload,
        criteriaResponse,
    ) {
        if (!setBitrixAccount || !setBitrixUpload) {
            return
        }

        sourceType.value = 'BITRIX_UPLOAD'
        setBitrixAccount(source.domain || null)

        const entityType = source.entity_type
            || (source.self_type === 'BitrixLeadsSource' && 'LEADS')
            || (source.self_type === 'BitrixContactsSource' && 'CONTACTS')
            || (source.self_type === 'BitrixDealsSource' && 'DEALS')
            || null

        const startDate = mapUploadDate({
            dateValue: source.date_from,
            offsetValue: source.offset_from,
            offsetType: source.offset_from_type,
        })
        const endDate = mapUploadDate({
            dateValue: source.date_to,
            offsetValue: source.offset_to,
            offsetType: source.offset_to_type,
        })

        setBitrixUpload({
            dataType: entityType,
            filter: {
                directions: entityType === 'DEALS'
                    ? mapIdOptions(source.direction_ids)
                    : [],
                stages: (entityType === 'DEALS' || entityType === 'LEADS')
                    ? mapIdOptions(source.stage_ids)
                    : [],
                conditionType: source.condition_type || 'AND',
                conditions: (entityType === 'DEALS' || entityType === 'LEADS')
                    ? mapBitrixConditions(source.conditions, criteriaResponse)
                    : [],
                dateField: source.date_field || 'DATE_CREATE',
                startDate,
                endDate,
            },
        })
    }

    function mapYandexSource(
        source,
        accounts,
        selectedYandexAccount,
        yandexUpload,
        sourceType,
        setYandexAccount,
        setYandexUpload
    ) {
        sourceType.value = 'YANDEX_DIRECT_STAT'

        const account = accounts.find(acc => acc.id === source.yandex_account)
        setYandexAccount(account)

        setYandexUpload({
            startDate: mapUploadDate({
                dateValue: source.date_from,
                offsetValue: source.offset_from,
                offsetType: source.offset_from_type,
            }),
            endDate: mapUploadDate({
                dateValue: source.date_to,
                offsetValue: source.offset_to,
                offsetType: source.offset_to_type,
            }),
        })
    }

    function mapVKSource(
        source,
        accounts,
        selectedVKAccount,
        vkAdsUpload,
        sourceType,
        setVKAccount,
        setVKUpload
    ) {
        sourceType.value = 'VKONTAKTE_CAMPAIGN_STAT'

        const account = accounts.find(acc => acc.id === source.vkontakte_account)
        setVKAccount(account)

        setVKUpload({
            startDate: mapUploadDate({
                dateValue: source.date_from,
                offsetValue: source.offset_from,
                offsetType: source.offset_from_type,
            }),
            endDate: mapUploadDate({
                dateValue: source.date_to,
                offsetValue: source.offset_to,
                offsetType: source.offset_to_type,
            }),
        })
    }

    function mapGoogleSheetSource(source, connection, sourceType) {
        sourceType.value = 'GOOGLE_SHEETS'
        connection.value.files_source.push({
            ...source,
            file: {
                id: source.spreadsheet_id,
                tab_id: source.tab_id,
                tab_name: source.tab_name,
                spreadsheet_name: source.spreadsheet_name,
            },
            tabs: [],
            id: source.spreadsheet_id,
            tab: { sheetId: source.tab_id, sheetTitle: source.tab_name },
            name: source.spreadsheet_name,
            isLoading: false
        })
    }

    function mapGoogleSheetTarget(target, connection) {
        connection.value.file_target = {
            ...target,
            file: {
                id: target.spreadsheet_id,
                tab_id: target.tab_id,
                tab_name: target.tab_name,
                spreadsheet_name: target.spreadsheet_name,
            },
            tabs: [],
            id: target.spreadsheet_id,
            tab: { sheetId: target.tab_id, sheetTitle: target.tab_name },
            name: target.spreadsheet_name,
            isLoading: false
        }
    }

    function mapFilters(filterResponse, connection, criteriaResponse) {
        connection.value.filter.conditions = []
        for (const condition of filterResponse.conditions) {
            connection.value.filter.conditions.push({
                column: {
                    column_index: condition.column_index,
                    column_letter: condition.column_letter,
                    column_value: condition.column_name
                },
                criteria: criteriaResponse.find(
                    item => item.criteria_type === condition.criteria
                ),
                filter_value: {
                    data: condition.value,
                    type: condition.value_type
                }
            })
        }
    }

    async function syncTabs(connection) {
        await Promise.all(
            connection.value.files_source.map(file => syncSourceTabs(file.id, connection))
        )
        await syncTargetTabs(connection)
    }

    async function syncSourceTabs(spreadsheet_id, connection) {
        const file_source = connection.value.files_source.find(
            file => file.id === spreadsheet_id
        )
        if (file_source) {
            file_source.isLoading = true
            file_source.tabs = await Api.getSpreadSheetTabs(spreadsheet_id)
            file_source.isLoading = false
        }
    }

    async function syncTargetTabs(connection) {
        if (connection.value.file_target.id) {
            connection.value.file_target.isLoading = true
            connection.value.file_target.tabs = await Api.getSpreadSheetTabs(
                connection.value.file_target.id
            )
            connection.value.file_target.isLoading = false
        }
    }

    return {
        isLoading,
        uploadOptions,
        loadConnection
    }
}
